/**
 * One team, specialist or agent on its own page: `/teams/:teamId` (tabs Overview, Settings, Details) and
 * `/teams/:teamId/members/:member` (an agent of a team: its Overview). The header carries who it is (its face or the
 * team's initials, its name with a pencil to rename it for those who may, its role and version); the tab is in the
 * address (`?tab=settings&part=agents`), so a part can be linked to and reached with Back.
 */

import type { MemberOverview } from '@aioe/shared';
import { ArrowLeft, Pencil } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { routes, type TeamWhereRow } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import { ErrorNote, inputClass } from '../ui.tsx';
import { ViewHeader, type ArtTone } from '../ui/Art.tsx';
import { FormField, FormSheet } from '../ui/FormSheet.tsx';
import { Hint } from '../ui/Hint.tsx';
import { PartBoundary } from '../ui/PartBoundary.tsx';
import { personInitials } from '../ui/PersonAvatar.tsx';
import { MemberFace } from './MemberFace.tsx';
import { plural } from './overviewModel.ts';
import { TeamOverview } from './TeamOverview.tsx';
import { isSettingsPart, TeamSettings, type SettingsPart } from './TeamSettings.tsx';
import { saveTeamChange } from './teamSave.ts';

type Tab = 'overview' | 'settings' | 'details';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'settings', label: 'Settings' },
  { id: 'details', label: 'Details' },
];

/** Rename a team or a specialist: the name people read; its id, its history and every machine's copy stay. */
function RenameSheet({ teamId, kind, name, onClose, onDone }: { teamId: string; kind: 'team' | 'specialist'; name: string; onClose: () => void; onDone: () => void }) {
  const [value, setValue] = useState(name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await saveTeamChange(teamId, kind, (d) => ({ ...d, name: value.trim() }));
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSheet
      title={`Rename ${name}`}
      description="Every machine that runs it shows the new name at its next refresh. Its history and its links stay."
      width="md"
      onClose={onClose}
      footer={
        <>
          <Button variant="tertiary" isDisabled={busy} onPress={onClose}>Cancel</Button>
          <Button isPending={busy} isDisabled={!value.trim() || value.trim() === name} onPress={() => void save()}>Rename</Button>
        </>
      }
    >
      <FormField label="Name">
        <input className={inputClass} value={value} maxLength={200} onChange={(e) => setValue(e.target.value)} autoFocus onKeyDown={(e) => e.key === 'Enter' && value.trim() && void save()} />
      </FormField>
      <ErrorNote error={error} />
    </FormSheet>
  );
}

/** The team's initials in its header: "SO" for Security Operations. */
const initials = (name: string) => personInitials(name).slice(0, 2).toUpperCase();

export function TeamScreen({ teamId, member, row, tone, details, mayEdit, onChanged }: { teamId: string; member?: string; row?: TeamWhereRow; tone: ArtTone; details?: ReactNode; mayEdit: boolean; onChanged: () => void }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [subject, setSubject] = useState<MemberOverview['subject']>();
  const [renaming, setRenaming] = useState(false);
  const raw = params.get('tab');
  const tab: Tab = member ? 'overview' : raw === 'settings' || (raw === 'details' && details) ? raw : 'overview';
  const part: SettingsPart = isSettingsPart(params.get('part')) ? (params.get('part') as SettingsPart) : 'agents';
  // Who it is: the Overview's answer when it is open, else the organisation's list (managers), else a small read.
  const known = subject && subject.teamId === teamId && (member ? subject.memberName?.toLowerCase() === member.toLowerCase() || subject.memberIds.some((id) => id.toLowerCase() === `agent:${teamId}/${member}`.toLowerCase()) : subject.kind !== 'member');
  const fallback = useLoad(() => (known || (row && !member) ? Promise.resolve(undefined) : routes.teamOverview(teamId, { range: '1h' }, member)), [teamId, member, known, !!row]);
  const s = known ? subject : fallback.data?.subject;
  const kind: 'team' | 'specialist' = (s?.kind === 'specialist' || row?.kind === 'specialist') && !member ? 'specialist' : 'team';
  const name = s?.name ?? row?.name ?? (member || 'Team');
  const editable = (s?.mayEdit ?? mayEdit) && !member;
  const go = (next: { tab?: Tab; part?: SettingsPart }) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        const t = next.tab ?? tab;
        if (t === 'overview') n.delete('tab');
        else n.set('tab', t);
        if (t === 'settings' && next.part) n.set('part', next.part);
        else if (t !== 'settings') n.delete('part');
        return n;
      },
      { replace: false },
    );

  const face = s?.face ?? (row?.kind === 'specialist' && row.faces?.[0] ? row.faces[0] : undefined);
  const icon = member || kind === 'specialist' ? (face ? <MemberFace member={face} /> : <span className="text-xs font-semibold">{initials(name)}</span>) : <span className="text-xs font-semibold">{initials(name)}</span>;
  const version = s?.version ?? row?.version;
  const blurb = (() => {
    if (member) return [s?.role, s?.teamName ? `in ${s.teamName}` : undefined, version !== undefined ? `version ${version}` : undefined].filter(Boolean).join(' · ');
    if (kind === 'specialist') return [s?.role ?? 'Specialist', version !== undefined ? `version ${version}` : undefined].filter(Boolean).join(' · ');
    const people = s?.members?.filter((m) => m.human).length ?? row?.roster?.filter((m) => m.human).length ?? 0;
    const all = s?.members?.length ?? row?.roster?.length ?? row?.members;
    const agents = all !== undefined ? all - people : undefined;
    return [agents !== undefined ? plural(agents, 'agent', 'agents') : undefined, people ? plural(people, 'person', 'people') : undefined, version !== undefined ? `version ${version}` : undefined].filter(Boolean).join(' · ');
  })();
  const tabs = TABS.filter((t) => t.id !== 'details' || details);

  return (
    <>
      <ViewHeader
        tone={tone}
        icon={icon}
        title={
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <span className="truncate">{name}</span>
            {editable ? (
              <Hint text={`Rename ${name}`}>
                <Button size="sm" variant="ghost" isIconOnly aria-label={`Rename ${name}`} className="text-muted" onPress={() => setRenaming(true)}>
                  <Pencil className="size-3.5" />
                </Button>
              </Hint>
            ) : null}
          </span>
        }
        blurb={blurb || undefined}
        actions={
          <Button size="sm" variant="secondary" onPress={() => navigate(member ? `/teams/${encodeURIComponent(teamId)}` : '/teams')}>
            <ArrowLeft className="size-4" />
            {member ? `Back to ${s?.teamName ?? 'the team'}` : 'All teams'}
          </Button>
        }
      />
      {member ? null : (
        <div className="-m-1 max-w-full overflow-x-auto p-1">
          <Segment key={tabs.length} aria-label={`${name}: which view`} size="sm" selectedKey={tab} onSelectionChange={(k) => go({ tab: String(k) as Tab })} className="w-fit min-w-max">
            {tabs.map((t) => (
              <Segment.Item key={t.id} id={t.id}>
                {t.label}
              </Segment.Item>
            ))}
          </Segment>
        </div>
      )}
      <PartBoundary key={`${teamId}:${member ?? ''}:${tab}`} name={tab === 'settings' ? 'Settings' : tab === 'details' ? 'Details' : 'Overview'} tone={tone}>
        {tab === 'overview' ? (
          <TeamOverview teamId={teamId} {...(member ? { member } : {})} tone={tone} onLoaded={(o) => setSubject(o.subject)} />
        ) : tab === 'settings' ? (
          <TeamSettings
            teamId={teamId}
            kind={kind}
            part={part}
            onPart={(p) => go({ tab: 'settings', part: p })}
            mayEdit={editable}
            tone={tone}
            onChanged={() => {
              setSubject(undefined);
              fallback.reload();
              onChanged();
            }}
          />
        ) : (
          details
        )}
      </PartBoundary>
      {renaming ? (
        <RenameSheet
          teamId={teamId}
          kind={kind}
          name={name}
          onClose={() => setRenaming(false)}
          onDone={() => {
            setSubject(undefined);
            fallback.reload();
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}
